import {I18n} from '@aws-amplify/core';
import {Badge} from '@mantine/core';

/**
 * FNP-918: invoice `status` as a badge (§13).
 *
 * ⚠️ Colours are Mantine THEME TOKENS, not hex literals. This file previously
 * hardcoded Bootstrap hex values ('#dc3545', '#28a745', …), which violates §2
 * "No hardcoded colours" — a hex value is fixed against whichever background
 * the author happened to be looking at, so the same badge that reads fine in
 * light mode loses contrast in dark. Theme tokens resolve per scheme.
 *
 * The mapping keeps the same semantics the hex values expressed:
 *   draft         grey   — not yet issued, no action taken
 *   open          blue   — issued and awaiting payment
 *   paid          green  — settled
 *   uncollectible red    — written off, the one that needs attention
 *   void          yellow — cancelled after issue
 *
 * ⚠️ Colour is NOT the only carrier of meaning — the badge always renders the
 * status TEXT, so it survives a colourblind reader and a greyscale print (§13
 * Accessibility).
 *
 * An unrecognised or absent status falls back to grey plus an explicit
 * placeholder rather than rendering an unstyled empty pill: Stripe can add
 * statuses, and a blank badge reads as a rendering bug.
 */
const STATUS_COLORS: Record<string, string> = {
    draft: 'gray',
    open: 'blue',
    paid: 'green',
    uncollectible: 'red',
    void: 'yellow',
};

export const StatusBadge = ({status}: {status?: string}) => {
    const color = (status && STATUS_COLORS[status]) || 'gray';

    return (
        <Badge color={color} variant='light'>
            {status ? I18n.get(status) : I18n.get('unknown')}
        </Badge>
    );
};
